// contexts/RadioContext.tsx
// Rewritten to align with the updated useWebRTC hook and the Dispatcher/Unit consoles.
// - Keeps a stable provider API for the UIs
// - Caches talkgroups/config with safe retry
// - Bridges device + PTT actions directly to useWebRTC
// - Provides audioLevels as zeros for compatibility (the UIs don't use meters)

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { useWebRTC, getRadioSocket } from '../hooks/useWebRTC';
import { http } from '../lib/http';

// --------- Module caches to avoid over-fetching ---------
let talkgroupsCache: any = null;
let configCache: any = null;
let talkgroupsPromise: Promise<any> | null = null;
let configPromise: Promise<any> | null = null;

// --------- Types ---------
interface Talkgroup {
  id: string;
  name: string;
  orgId: string;
  isAllCall: boolean;
  policy: {
    TOT: number;
    allowUnitTx: boolean;
    maxSimulTx: number;
  };
}

interface RadioUser {
  id: string;
  sub: string;
  role: 'dispatcher' | 'unit';
  displayName: string;
  orgId: string;
  priority: number;
  talkgroupAssignments: string[];
  claimedUnit?: string; // Unit ID that this user has claimed
  unitStatus?: string; // Status of the claimed unit
}

interface RadioConfig {
  opus: {
    sampleRate: number;
    channels: number;
    bitrateKbps: number;
    ptimeMs: number;
    fec: boolean;
    dtx: boolean;
  };
  policies: {
    TOTs: {
      defaultSec: number;
    };
    maxSimulSpeakersPerTG: number;
    dispatcherPreempt: boolean;
  };
  observer: {
    vadThresholdDb: number;
    activationMs: number;
    decayMs: number;
  };
  dispatcher: {
    maxActiveDecodes: number;
  };
  network: {
    stun: string[];
    turn: string[];
  };
  recording: {
    mode: string;
    retentionDays: number;
  };
}

interface P25VocoderConfig {
  mode: 'P25_PHASE1' | 'P25_PHASE2' | 'CUSTOM';
  bitDepth: number;
  compressionRatio: number;
  noiseLevel: number;
  frameSize: number;
  spectralBoost: number;
  dropoutRate: number;
}

interface RadioContextType {
  // Connection state
  isConnected: boolean;
  isInitialized: boolean;
  user: RadioUser | null;
  error: string | null;

  // Talkgroups
  talkgroups: Talkgroup[];
  activeTalkgroups: Map<string, any[]>;
  transmissionLogs: Map<string, any[]>;

  // User management
  connectedUsers: Map<string, any>;
  userTalkgroupAssignments: Map<string, string[]>; // userId -> talkgroupIds
  currentUserChannel: string | null; // Current user's active channel

  // Configuration
  config: RadioConfig | null;

  // PTT state (for timers/lights)
  isTransmitting: boolean;
  currentTalkgroup: string | null;
  transmissionStartTime: number | null;

  // Audio levels (kept for API compatibility; the UIs above don't use meters)
  audioLevels: {
    inputLevel: number;
    outputLevel: number;
  };

  // Devices
  devices: any[];
  selectedInputDevice: string | null;
  selectedOutputDevice: string | null;

  // TX FX controls
  txFxEnabled: boolean;
  setTxFxEnabled: (enabled: boolean) => void;

  // P25 Vocoder controls
  p25VocoderConfig: P25VocoderConfig;
  setP25VocoderMode: (mode: 'P25_PHASE1' | 'P25_PHASE2' | 'CUSTOM') => void;
  setP25VocoderConfig: (config: Partial<P25VocoderConfig>) => void;
  P25_PRESETS: Record<string, P25VocoderConfig>;

  // Actions
  initialize: (token: string) => Promise<void>;
  startTransmitting: (talkgroupId: string) => Promise<void>;
  stopTransmitting: () => Promise<void>;
  subscribeToTalkgroup: (talkgroupId: string) => Promise<void>;
  unsubscribeFromTalkgroup: (talkgroupId: string) => Promise<void>;
  updateDevice: (type: 'input' | 'output', deviceId: string) => void;
  refreshTalkgroups: () => Promise<void>;
  refreshConfig: () => Promise<void>;
  testAudioPlayback: () => void;
  playTalkPermitSound: () => void;
  setTalkgroupVolume: (talkgroupId: string, volume: number) => void;
  setTalkgroupMuted: (talkgroupId: string, muted: boolean) => void;
  playTransmissionAudio: (logId: string) => void;

  // User management actions
  moveUserToTalkgroup: (userId: string, talkgroupId: string) => Promise<void>;
  removeUserFromTalkgroup: (userId: string, talkgroupId: string) => Promise<void>;
  refreshConnectedUsers: () => Promise<void>;
  
  // Unit status utilities
  getUnitStatusColor: (status: string) => string;
}

const RadioContext = createContext<RadioContextType | undefined>(undefined);

interface RadioProviderProps {
  children: ReactNode;
  serverUrl?: string;
}

export const RadioProvider: React.FC<RadioProviderProps> = ({
  children,
  serverUrl,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [talkgroups, setTalkgroups] = useState<Talkgroup[]>([]);
  const [config, setConfig] = useState<RadioConfig | null>(null);
  
  // User management state
  const [connectedUsers, setConnectedUsers] = useState<Map<string, any>>(new Map());
  const [userTalkgroupAssignments, setUserTalkgroupAssignments] = useState<Map<string, string[]>>(new Map());
  const [currentUserChannel, setCurrentUserChannel] = useState<string | null>(null);
  const radioSocket = getRadioSocket();

  // Functions to manage current user's active channel
  const updateCurrentUserChannel = useCallback((talkgroupId: string) => {
    console.log(`[RadioContext] Setting current user channel to: ${talkgroupId}`);
    setCurrentUserChannel(talkgroupId);
  }, []);

  // Memoize config for the hook
  const webRTCConfig = useMemo(
    () => ({
      serverUrl: serverUrl || '',
      token: token || '',
    }),
    [serverUrl, token]
  );

  const webRTC = useWebRTC(webRTCConfig, talkgroups, {
    setCurrentUserChannel: updateCurrentUserChannel,
  });

  // --------- Data fetchers with caching ---------
  const refreshTalkgroups = useCallback(async () => {
    if (talkgroupsCache) {
      setTalkgroups(talkgroupsCache);
      return;
    }
    if (talkgroupsPromise) {
      const data = await talkgroupsPromise;
      setTalkgroups(data);
      return;
    }

    talkgroupsPromise = (async () => {
      try {
        const res = await http.get('/radio/talkgroups');

        if (res.status === 200) {
          const data = res.data;
          talkgroupsCache = data;
          return data;
        }

        if (res.status === 429) {
          await new Promise((r) => setTimeout(r, 3000));
          const retry = await http.get('/radio/talkgroups');
          if (retry.status === 200) {
            const data = retry.data;
            talkgroupsCache = data;
            return data;
          }
        }

        throw new Error(`Failed to fetch talkgroups: ${res.status}`);
      } finally {
        talkgroupsPromise = null;
      }
    })();

    const data = await talkgroupsPromise;
    setTalkgroups(data);
  }, [serverUrl]);

  const refreshConfig = useCallback(async () => {
    if (configCache) {
      setConfig(configCache);
      return;
    }
    if (configPromise) {
      const data = await configPromise;
      setConfig(data);
      return;
    }

    configPromise = (async () => {
      try {
        const res = await fetch(`${serverUrl}/api/radio/config`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (res.ok) {
          const data = await res.json();
          configCache = data;
          return data;
        }

        if (res.status === 429) {
          await new Promise((r) => setTimeout(r, 3000));
          const retry = await fetch(`${serverUrl}/api/radio/config`);
          if (retry.ok) {
            const data = await retry.json();
            configCache = data;
            return data;
          }
        }

        throw new Error(`Failed to fetch radio config: ${res.status}`);
      } finally {
        configPromise = null;
      }
    })();

    const data = await configPromise;
    setConfig(data);
  }, [serverUrl]);

  // --------- Initialize (token + bootstrap data) ---------
  const initialize = useCallback(
    async (newToken: string) => {
      setToken(newToken);

      try {
        // Initialize WebRTC with token for socket authentication
        await webRTC.initialize(newToken);
        
        await refreshTalkgroups();
        // small delay to reduce chance of 429s when booting cold
        await new Promise((r) => setTimeout(r, 750));
        await refreshConfig();
      } catch (err) {
        // logged upstream; keep UI usable
        // eslint-disable-next-line no-console
        console.error('Failed to initialize radio system:', err);
      }
    },
    [webRTC, refreshTalkgroups, refreshConfig]
  );

  // --------- User Management Functions ---------
  const refreshConnectedUsers = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${serverUrl}/api/radio/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[User Management] Received data:', {
          users: data.users,
          assignments: data.assignments
        });
        const newConnectedUsers = new Map(data.users.map((u: any) => [u.id, u])) as Map<string, any>;
        const newUserTalkgroupAssignments = new Map(data.assignments.map((a: any) => [a.userId, a.talkgroupIds])) as Map<string, string[]>;
        console.log('[User Management] Setting new userTalkgroupAssignments:', newUserTalkgroupAssignments);
        setConnectedUsers(newConnectedUsers);
        setUserTalkgroupAssignments(newUserTalkgroupAssignments);
      }
    } catch (error) {
      console.error('Failed to refresh connected users:', error);
    }
  }, [serverUrl, token]);

  const moveUserToTalkgroup = useCallback(async (userId: string, talkgroupId: string) => {
    if (!token) return;
    
    console.log('[RadioContext] moveUserToTalkgroup called:', {
      userId,
      talkgroupId,
      currentUser: webRTC.user?.id,
      currentUserSub: webRTC.user?.sub,
      currentUserTalkgroupAssignments: userTalkgroupAssignments?.get(userId)
    });
    
    try {
      const response = await fetch(`${serverUrl}/api/radio/users/${userId}/channel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ talkgroupId })
      });
      
      if (response.ok) {
        console.log('[RadioContext] Channel change API call successful');
        // Only refresh connected users if the current user is a dispatcher
        if (webRTC.user?.role === 'dispatcher') {
          console.log('[RadioContext] Refreshing connected users after channel change');
          await refreshConnectedUsers();
        }
      } else {
        console.error('Failed to set user channel:', response.statusText);
      }
    } catch (error) {
      console.error('Failed to set user channel:', error);
    }
  }, [serverUrl, token, refreshConnectedUsers, webRTC.user]);

  const removeUserFromTalkgroup = useCallback(async (userId: string, talkgroupId: string) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${serverUrl}/api/radio/users/${userId}/talkgroups/${talkgroupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        await refreshConnectedUsers();
      } else {
        console.error('Failed to remove user from talkgroup:', response.statusText);
      }
    } catch (error) {
      console.error('Failed to remove user from talkgroup:', error);
    }
  }, [serverUrl, token, refreshConnectedUsers]);

  // Unit status color utility function
  const getUnitStatusColor = useCallback((status: string) => {
    // Using exact colors from Config.lua UNIT_COLOR
    switch (status) {
      case 'I/S': return '#32be5a'; // Color3.fromRGB(50,190,90)
      case 'DISP': return '#ffd25a'; // Color3.fromRGB(255,210,90)
      case 'E/R': return '#f0b432'; // Color3.fromRGB(240,180,50)
      case 'O/S': return '#3c96ff'; // Color3.fromRGB(60,150,255)
      case 'TRX': return '#c896ff'; // Color3.fromRGB(200,150,255)
      case 'TRX ARR': return '#b478f0'; // Color3.fromRGB(180,120,240)
      case 'OOS': return '#a0a0a0'; // Color3.fromRGB(160,160,160)
      case 'OOS-GAME': return '#787878'; // Color3.fromRGB(120,120,120)
      default: return '#a0a0a0';
    }
  }, []);

  // Initialize current user channel from JWT token on login (use first assignment as default)
  useEffect(() => {
    if (webRTC.user?.talkgroupAssignments && webRTC.user.talkgroupAssignments.length > 0) {
      const defaultChannel = webRTC.user.talkgroupAssignments[0];
      console.log(`[RadioContext] Initializing current user channel from JWT:`, defaultChannel);
      setCurrentUserChannel(defaultChannel);
    }
  }, [webRTC.user?.talkgroupAssignments]);

  // WebSocket event listeners for user management updates
  useEffect(() => {
    if (!radioSocket) return;

    // Listen for user connection/disconnection events
    const handleUserConnect = (userData: any) => {
      console.log('[RadioContext] User connected:', userData);
      // Refresh user list when someone connects
      refreshConnectedUsers();
    };

    const handleUserDisconnect = (userData: any) => {
      console.log('[RadioContext] User disconnected:', userData);
      // Refresh user list when someone disconnects
      refreshConnectedUsers();
    };

    // Listen for user channel changes
    const handleUserChannelChange = (data: any) => {
      console.log('[RadioContext] User channel changed event received:', data);
      console.log('[RadioContext] Current userTalkgroupAssignments before refresh:', userTalkgroupAssignments);
      // Refresh user list when someone's channel changes
      refreshConnectedUsers();
    };

    // Listen for unit claim updates from CAD system
    const handleUnitClaimUpdated = (data: any) => {
      console.log('[RadioContext] Unit claim updated event received:', data);
      // Refresh user list when unit claims change
      refreshConnectedUsers();
    };

    // Listen for unit status updates from CAD system
    const handleUnitStatusUpdated = (data: any) => {
      console.log('[RadioContext] Unit status updated event received:', data);
      // Refresh user list when unit status changes
      refreshConnectedUsers();
    };

    radioSocket.on('user:connected', handleUserConnect);
    radioSocket.on('user:disconnected', handleUserDisconnect);
    radioSocket.on('user:channelChanged', handleUserChannelChange);
    radioSocket.on('unit-claim-updated', handleUnitClaimUpdated);
    radioSocket.on('unit-status-updated', handleUnitStatusUpdated);
    
    // Debug: Log all WebSocket events to see what's being received
    radioSocket.onAny((eventName, ...args) => {
      if (eventName.startsWith('user:')) {
        console.log(`[RadioContext] WebSocket event received: ${eventName}`, args);
      }
    });

    return () => {
      radioSocket.off('user:connected', handleUserConnect);
      radioSocket.off('user:disconnected', handleUserDisconnect);
      radioSocket.off('user:channelChanged', handleUserChannelChange);
      radioSocket.off('unit-claim-updated', handleUnitClaimUpdated);
      radioSocket.off('unit-status-updated', handleUnitStatusUpdated);
      radioSocket.offAny();
    };
  }, [radioSocket, refreshConnectedUsers]);

  // --------- Talkgroup list for user role ---------
  const getUserTalkgroups = () => {
    if (!webRTC.user || !talkgroups.length) return [];

    if (webRTC.user.role === 'dispatcher') {
      // Dispatcher sees everything, including ALL-CALL
      return talkgroups;
    }

    // Unit: hide ALL-CALL
    return talkgroups.filter((tg) => !tg.isAllCall);
  };

  // --------- Context value ---------
  const contextValue: RadioContextType = useMemo(
    () => ({
      // Connection
      isConnected: webRTC.isConnected,
      isInitialized: webRTC.isInitialized,
      user: webRTC.user,
      error: webRTC.error,

      // TG state
      talkgroups: getUserTalkgroups(),
      activeTalkgroups: webRTC.activeTalkgroups,
      transmissionLogs: webRTC.transmissionLogs,

      // User management
      connectedUsers,
      userTalkgroupAssignments,

      // Config
      config,

      // PTT state bridge
      isTransmitting: webRTC.pttState.isTransmitting,
      currentTalkgroup: webRTC.pttState.talkgroupId,
      transmissionStartTime: webRTC.pttState.startTime,

      // Audio meters (kept for compatibility with older components)
      audioLevels: { inputLevel: 0, outputLevel: 0 },

      // Devices
      devices: webRTC.devices,
      selectedInputDevice: webRTC.selectedInputDevice,
      selectedOutputDevice: webRTC.selectedOutputDevice,

      // TX FX controls
      txFxEnabled: webRTC.txFxEnabled,
      setTxFxEnabled: webRTC.setTxFxEnabled,

      // P25 Vocoder controls
      p25VocoderConfig: webRTC.p25VocoderConfig,
      setP25VocoderMode: webRTC.setP25VocoderMode,
      setP25VocoderConfig: webRTC.setP25VocoderConfig,
      P25_PRESETS: webRTC.P25_PRESETS,

      // Actions
      initialize,
      startTransmitting: webRTC.startTransmitting,
      stopTransmitting: webRTC.stopTransmitting,
      subscribeToTalkgroup: webRTC.subscribeToTalkgroup,
      unsubscribeFromTalkgroup: webRTC.unsubscribeFromTalkgroup,
      updateDevice: webRTC.updateDevice,
      refreshTalkgroups,
      refreshConfig,
      testAudioPlayback: webRTC.testAudioPlayback,
      playTalkPermitSound: webRTC.playTalkPermitSound,
      setTalkgroupVolume: webRTC.setTalkgroupVolume,
      setTalkgroupMuted: webRTC.setTalkgroupMuted,
      playTransmissionAudio: webRTC.playTransmissionAudio,

      // User management actions
      moveUserToTalkgroup,
      removeUserFromTalkgroup,
      refreshConnectedUsers,
      currentUserChannel,
      
      // Unit status utilities
      getUnitStatusColor,
      
      // Talkgroup settings
      talkgroupVolumes: webRTC.talkgroupVolumes,
      mutedTalkgroups: webRTC.mutedTalkgroups,
    }),
    [
      webRTC.isConnected,
      webRTC.isInitialized,
      webRTC.user,
      webRTC.error,
      webRTC.activeTalkgroups,
      webRTC.transmissionLogs,
      connectedUsers,
      userTalkgroupAssignments,
      webRTC.pttState.isTransmitting,
      webRTC.pttState.talkgroupId,
      webRTC.pttState.startTime,
      webRTC.devices,
      webRTC.selectedInputDevice,
      webRTC.selectedOutputDevice,
      webRTC.txFxEnabled,
      webRTC.setTxFxEnabled,
      webRTC.p25VocoderConfig,
      webRTC.setP25VocoderMode,
      webRTC.setP25VocoderConfig,
      webRTC.P25_PRESETS,
      config,
      initialize,
      webRTC.startTransmitting,
      webRTC.stopTransmitting,
      webRTC.subscribeToTalkgroup,
      webRTC.unsubscribeFromTalkgroup,
      webRTC.updateDevice,
      refreshTalkgroups,
      refreshConfig,
      webRTC.testAudioPlayback,
      webRTC.playTalkPermitSound,
      webRTC.setTalkgroupVolume,
      webRTC.setTalkgroupMuted,
      webRTC.playTransmissionAudio,
      moveUserToTalkgroup,
      removeUserFromTalkgroup,
      refreshConnectedUsers,
      currentUserChannel,
      getUnitStatusColor,
      webRTC.talkgroupVolumes,
      webRTC.mutedTalkgroups,
    ]
  );

  return <RadioContext.Provider value={contextValue}>{children}</RadioContext.Provider>;
};

export const useRadio = (): RadioContextType => {
  const ctx = useContext(RadioContext);
  if (!ctx) {
    throw new Error('useRadio must be used within a RadioProvider');
  }
  return ctx;
};
